/* eslint-disable ember/no-runloop */
import type Store from "@ember-data/store";
import type ModelRegistry from "@ember-data/types/registries/model";
import { assert } from "@ember/debug";
import { next } from "@ember/runloop";
import { service } from "@ember/service";
import { Changeset } from "ember-changeset";
import lookupValidator from "ember-changeset-validations";
import type {
  BufferedChangeset,
  ValidatorAction,
  ValidatorMap
} from "ember-changeset/types";
import type FlashMessagesService from "ember-cli-flash/services/flash-messages";
import ForcBaseComponent from "parog-web/components/forc/abstract/base-component";
import DraftManager from "parog-web/components/forc/draft-manager";
import FormAction from "parog-web/components/forc/form-action";
import type FileAttachment from "parog-web/models/file-attachment";
import type JournalEventModel from "parog-web/models/journal-event";
import type Patient from "parog-web/models/patient";
import type AnalyticsService from "parog-web/services/analytics";
import type { ForcBaseArgs } from "./base-component";

export interface ToastMessage {
  title?: string;
  message: string;
}

export interface Payload extends ForcModelPojo {
  fileAttachments?: FileAttachment[];
  patient?: Patient | null;
}

export type ForcModelPojo = Record<string, unknown>;

export default class ForcJournalEventBasedComponent<
  Args extends ForcBaseArgs = ForcBaseArgs
> extends ForcBaseComponent<Args> {
  /**
   * This property is used to display the forc display name in the timeline or other places,
   * through formWithId hook in parogXX/addon/index.js
   * At launch, the `forc-registration` instance-initializer automatically find and add those forc components
   * to staticForms, so that they can be retrieved as usual forms
   *
   * Thus, you must define this property if you want your forc to be reachable from formWithId.
   */
  static forcDisplayName: string;
  /**
   * The full component id, containing the feature name and the component name,
   * for instance "online-consultation/report-edition-form".
   * Ideally, this should be injected by Ember CLI, or the registry, but there
   * is no registry for components, component names are dynamically resolved at
   * runtime when instantiated.
   *
   * Thus, this must be set by developers when inheriting from this class.
   */
  static fullComponentId: string;
  /**
   * The corresponding model name.
   * TODO: Typescript JournalEventClass generator
   */
  static journalEventClass;

  @service declare analytics: AnalyticsService;
  @service declare flashMessages: FlashMessagesService;
  @service requestManager;
  @service declare store: Store;

  /**
   * @type {DraftManager}
   * @private
   */
  draft;
  /**
   * Describes the journal event's properties that will be used for load and save.
   */
  journalEventProperties: Array<string> = [];
  /**
   * If `true`, the content of the changeset is regularly saved in the local
   * storage, and automatically injected when the component is loaded in `new`
   * mode with the same patient and form id.
   */
  shouldAutoSaveDraft = false;
  /**
   * An Ember Changeset validator.
   * If set, it is used instead of the default `lookupValidator(this.validations)`.
   */
  validator?: ValidatorAction;

  get changeset(): BufferedChangeset {
    const changeset = this.computeChangeset();

    // Memoization :
    Object.defineProperty(this, "changeset", {
      value: changeset,
      writable: false
    });

    this.draft = new DraftManager(this);
    // The load of the draftManager must be called after the "memoization" is done.
    // `this.computeChangeset` creates a Changeset object and consumes its @tracked `_changes` property
    // calling `this.draft.load();` will call changeset.restore and will modified the @tracked `_changes` property
    // This can't be done in the same runloop or you'll get the error
    next(this, function () {
      this.draft.load();
      this.afterLoad();
    });

    return this.changeset;
  }

  get defaultModel() {
    return {};
  }

  /// PRIVATE PROPERTIES
  get forcDisplayName(): string {
    return (this.constructor as typeof ForcJournalEventBasedComponent)
      .forcDisplayName;
  }

  get fullComponentId(): string {
    const fullComponentId = (
      this.constructor as typeof ForcJournalEventBasedComponent
    ).fullComponentId;
    assert(
      `The 'fullComponentId' is not defined for this ForcJournalEventBasedComponent subclass`,
      Boolean(fullComponentId)
    );
    return fullComponentId;
  }

  override get isDirty(): boolean {
    const chatMessageKeys = ["chatMessage", "willSendMessage"];
    return this.changeset.changes.length == 2 &&
      chatMessageKeys.includes(this.changeset.changes[0].key) &&
      chatMessageKeys.includes(this.changeset.changes[1].key)
      ? false
      : this.changeset.get("isDirty");
  }

  get isEditMode(): boolean {
    return this.formAction == FormAction.Edit;
  }

  /**
   * Will be set by the 'retrieveModel' method if this form has been loaded
   * from an existing je.
   * TODO: Actually this is based on the 'retrievedJournalEvent' accessor which
   *       make a request to the store each time this is called.
   *       We should instead store the retrievedJe or store this in a variable.
   */
  get isLoadedFromJe(): boolean {
    return Boolean(this.retrievedJournalEvent);
  }

  /// USABLE INTERFACE
  get isNewMode(): boolean {
    // We suppose that if we do not have formAction provided we are in new mode.
    return this.formAction == FormAction.New || this.formAction == null;
  }

  get journalEventClass(): any {
    return (this.constructor as typeof ForcJournalEventBasedComponent)
      .journalEventClass;
  }

  get journalEventClassType(): keyof ModelRegistry {
    return this.journalEventClass.type;
  }

  get model(): ForcModelPojo | JournalEventModel {
    const model = this.retrieveModel();

    // This is the funny memoization pattern.
    // We can't do "delete this.model" because the accessor is defined in the prototype.
    // So we just have to set the property in the instance like this.
    Object.defineProperty(this, "model", {
      value: model,
      writable: false // if one day we need to set the model somewhere else we can set to true
    });

    return this.model;
  }

  get retrievedJournalEvent(): JournalEventModel | undefined {
    return this.retrieveJournalEvent();
  }

  get sourceJeId() {
    return this.args.sourceJeId;
  }

  get sourceJournalEvent(): JournalEventModel | null {
    if (this.sourceJeId) {
      const je = this.store.peekRecord(
        this.journalEventClassType,
        this.sourceJeId
      ) as JournalEventModel;
      if (je) {
        assert(
          "The patient should be the same as the one in the component.",
          je.patient?.get("id") == this.patient?.id
        );
        return je;
      }
      return null;
    }
    return null;
  }

  /**
   * Need to be override by the FORC if we want a custom flash message
   * If you won't a flash message, override it and return null
   *
   */
  get toastMessage(): ToastMessage | null {
    return {
      message: "Information bien enregistrée. Merci !"
    };
  }

  /// STATE
  /**
   * An Ember Changeset Validations object
   */
  get validations(): ValidatorMap {
    return {};
  }

  static hasDraftForPatient(patient: Patient): boolean {
    return DraftManager.hasDraftFor(
      DraftManager.localStorageKey(this.fullComponentId, patient)
    );
  }

  afterLoad(): void {
    return;
  }

  /// IMPLEMENTATION
  /**
   * Compute the changeset based on the current model.
   * This method is only called once when the changeset is accessed for the first time.
   * It's possible to override it to add behavior to the changeset.
   */
  computeChangeset(): BufferedChangeset {
    const validations = this.validations;
    const changeset = Changeset(
      this.model,
      this.validator ? this.validator : lookupValidator(validations),
      validations
    );

    changeset.on("afterValidation", () => {
      this.draft.onDidUpdate();
    });

    return changeset;
  }

  /**
   * A payload based on the `journalEventProperties`
   */
  computePayload(): Payload {
    return this.journalEventProperties.reduce(
      (acc, property) => {
        acc[property] = this.model[property];
        return acc;
      },
      { patient: this.patient }
    );
  }

  override async isValid(): Promise<boolean> {
    await this.changeset.validate();
    return this.changeset.get("isValid");
  }

  popToast() {
    if (this.toastMessage)
      this.flashMessages.success(this.toastMessage.message, {
        title: this.toastMessage.title
      });
  }

  /**
   * Describes the strategy used to retrieve the Journal Event that will be used as a model.
   * Default strategies can be found in `journal-event-retrieval-strategies`.
   *
   */
  retrieveJournalEvent(): JournalEventModel | undefined {
    return;
  }

  /**
   * The model will be mutated so do not return a JournalEvent from the store but a copy!
   * By default return the last JournalEvent of the given type.
   * This method is only called once when the model is accessed for the first time.
   */
  retrieveModel(): ForcModelPojo {
    const journalEvent = this.retrievedJournalEvent || this.defaultModel;
    return this.journalEventProperties.reduce((acc, property) => {
      acc[property] = journalEvent[property];
      return acc;
    }, {});
  }

  override async save(): Promise<typeof this.journalEventClass | undefined> {
    if (!this.isDirty) {
      return;
    }
    let savedModel;

    this.changeset.execute();
    if (this.formAction === FormAction.Edit) {
      const sourceJournalEvent = this.retrievedJournalEvent;
      if (sourceJournalEvent) {
        await sourceJournalEvent.archive();
      }

      savedModel = await this.saveModel();

      if (sourceJournalEvent && sourceJournalEvent.fileAttachments.length > 0)
        await this.transferFileAttachments(
          sourceJournalEvent.fileAttachments,
          savedModel
        );
    } else {
      savedModel = await this.saveModel();
    }

    await this.draft.onDidSave();
    this.popToast();
    return savedModel;
  }

  async saveModel(): Promise<typeof this.journalEventClass> {
    // TODO: Create the changeset from a new JournalEvent and then save it
    // Think about removing all the JE with 'isNew' from the store.

    const payload = this.computePayload();

    const journalEventRecord = this.store.createRecord(
      this.journalEventClassType,
      payload
    );

    const newJe = await journalEventRecord.save();

    return newJe;
  }

  async transferFileAttachments(
    fileAttachments: NonNullable<Payload["fileAttachments"]>,
    newJe: JournalEventModel
  ): Promise<void> {
    await Promise.all(
      fileAttachments.slice().map(async f => {
        // @ts-expect-error journalEvent is declared as AsyncBelongsTo.
        //   When settings the value, from TypeScript point-of-view,
        //   a promise should be set, while setting the value directly
        //   is actually supported  by Ember Data, and is the official way.
        f.journalEvent = newJe;
        await f.save();
      })
    );
  }
}
